import type { Program } from "@coral-xyz/anchor";
import { BN } from "@coral-xyz/anchor";
import { TOKEN_PROGRAM_ID } from "@solana/spl-token";
import type { Connection, TransactionInstruction } from "@solana/web3.js";
import { PublicKey, SystemProgram } from "@solana/web3.js";
import type { RewardCallOptionsIDL } from "@stepfinance/common";
import { fromLamports, STEP_MINT } from "@stepfinance/common";

import { confirmOptions } from "../../../constants/transaction";
import type { CurrentLanguageSourceType } from "../../../contexts/language/imports";
import type { CommonNotificationsStrings } from "../../../contexts/language/languageTypes";
import type { RewardOption } from "../../../hooks";
import type { ConnectedWallet } from "../../../hooks/wallet/useConnectedWallet";
import { generateOneToOneNotificationDescription } from "../../../utils/notifications";
import { sendTransaction } from "../../../utils/transactions";
import { findOrCreateAccountByMint } from "../../account";

type ExerciseNotificaions =
  CurrentLanguageSourceType["pages"]["dashboard"]["modules"]["rewardOptions"]["notifications"] &
    CommonNotificationsStrings;

export const exerciseOption = async (
  connection: Connection,
  program: Program<RewardCallOptionsIDL>,
  rewardOption: RewardOption,
  owner: ConnectedWallet,
  userPaymentAccount: PublicKey,
  exerciseAmount: number,
  exerciseTotal: number,
  onTransactionPending: () => void,
  notificationText: ExerciseNotificaions,
) => {
  const {
    index: claimIndex,
    rewardVault,
    priceVault,
    claimsBitMaskAccount,
    distributorAddress,
    amount: authorizedAmount,
    proof,
    rewardMint,
    priceMint,
    rewardDecimals,
  } = rewardOption;

  const distAddress = new PublicKey(distributorAddress);
  const instructions: TransactionInstruction[] = [];
  const cleanupInstructions: TransactionInstruction[] = [];
  const userRewardAccount = await findOrCreateAccountByMint(
    connection,
    owner.publicKey,
    owner.publicKey,
    instructions,
    new PublicKey(STEP_MINT),
  );

  instructions.push(
    program.instruction.exercise(
      new BN(claimIndex),
      new BN(authorizedAmount),
      new BN(exerciseAmount),
      proof.map((a) => Buffer.from(a, "base64")),
      {
        accounts: {
          distributor: distAddress,
          claimsBitmaskAccount: claimsBitMaskAccount,
          rewardVault: rewardVault,
          priceVault: priceVault,
          userRewardTokenAccount: userRewardAccount,
          userPaymentAccount: userPaymentAccount,
          userPaymentAuthority: owner.publicKey,
          claimant: owner.publicKey,
          payer: owner.publicKey,
          systemProgram: SystemProgram.programId,
          tokenProgram: TOKEN_PROGRAM_ID,
        },
        signers: [],
      },
    ),
  );

  return sendTransaction(
    connection,
    owner,
    instructions.concat(cleanupInstructions),
    [],
    true,
    confirmOptions,
    true,
    {
      approveMessage: notificationText.approveTransaction,
      success: {
        message: notificationText.exercisedRewardOption.message,
        description: generateOneToOneNotificationDescription({
          tokenA: {
            action: notificationText.tokenBalanceChanges.paid,
            amount: exerciseTotal,
            mint: priceMint,
            type: "decrease",
          },
          tokenB: {
            action: notificationText.tokenBalanceChanges.received,
            amount: fromLamports(exerciseAmount, rewardDecimals),
            mint: rewardMint,
            type: "increase",
          },
        }),
      },
    },
    onTransactionPending,
  );
};
